// Utilidades para búsqueda y caché de iconos Iconify
import React from 'react';
import { Icon } from '@iconify/react';
import { Box } from '@mui/material';
import { config } from '../config';

// Interfaces
export interface IconifyIcon {
  name: string; // formato: "mdi:chef-hat"
  collection: string; // "mdi", "heroicons", etc.
  displayName?: string;
  category?: string;
}

export interface CachedIcon {
  iconId: string; // formato: "mdi:chef-hat"
  svg: string; // SVG como string
  cachedAt: number; // timestamp
}

// Clave para localStorage
const CACHE_KEY = 'iconify_icons_cache';
const CACHE_VERSION = 1;
const MAX_CACHE_SIZE = 500; // Máximo de iconos en caché

// Clave para caché de traducciones
const TRANSLATION_CACHE_KEY = 'iconify_translations_cache';
const TRANSLATION_CACHE_VERSION = 1;

// ============================================
// CACHÉ LOCAL (localStorage)
// ============================================

/**
 * Obtener todos los iconos cacheados
 */
export const getCachedIcons = (): Map<string, CachedIcon> => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return new Map();
    
    const data = JSON.parse(cached);
    if (data.version !== CACHE_VERSION) {
      // Limpiar caché si la versión cambió
      localStorage.removeItem(CACHE_KEY);
      return new Map();
    }
    
    return new Map(data.icons);
  } catch (error) {
    console.error('Error leyendo caché de iconos:', error);
    return new Map();
  }
};

/**
 * Guardar icono en caché
 */
export const cacheIcon = async (iconId: string): Promise<boolean> => {
  try {
    // Verificar si ya está en caché
    const cached = getCachedIcons();
    if (cached.has(iconId)) {
      return true; // Ya está cacheado
    }
    
    // Descargar SVG desde Iconify API
    const svg = await downloadIconSVG(iconId);
    if (!svg) {
      return false;
    }
    
    // Agregar a caché
    cached.set(iconId, {
      iconId,
      svg,
      cachedAt: Date.now()
    });
    
    // Limitar tamaño de caché (eliminar los más antiguos si excede)
    if (cached.size > MAX_CACHE_SIZE) {
      const sorted = Array.from(cached.entries())
        .sort((a, b) => a[1].cachedAt - b[1].cachedAt);
      
      // Eliminar los más antiguos
      const toRemove = sorted.slice(0, cached.size - MAX_CACHE_SIZE);
      toRemove.forEach(([id]) => cached.delete(id));
    }
    
    // Guardar en localStorage
    const data = {
      version: CACHE_VERSION,
      icons: Array.from(cached.entries())
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    
    return true;
  } catch (error) {
    console.error('Error cacheando icono:', error);
    return false;
  }
};

/**
 * URL de SVG en Iconify: /{prefix}/{name}.svg.
 * La forma /{prefix}:{name}.svg dejó de resolver en Iconify API 3.2 (404).
 */
export const iconifySvgUrl = (iconId: string): string => {
  const colon = iconId.indexOf(':');
  if (colon > 0 && colon < iconId.length - 1) {
    const prefix = iconId.slice(0, colon);
    const name = iconId.slice(colon + 1);
    return `https://api.iconify.design/${encodeURIComponent(prefix)}/${encodeURIComponent(name)}.svg`;
  }
  return `https://api.iconify.design/${encodeURIComponent(iconId)}.svg`;
};

/**
 * SVG desde el paquete @iconify-icons en jsDelivr.
 * No usa el cupo de api.iconify.design, que responde 429 si se llena.
 */
const svgFromIconifyPackage = async (iconId: string): Promise<string | null> => {
  const colon = iconId.indexOf(':');
  if (colon <= 0 || colon >= iconId.length - 1) {
    return null;
  }
  const prefix = iconId.slice(0, colon);
  const name = iconId.slice(colon + 1);
  if (!/^[\w-]+$/.test(prefix) || !/^[\w-]+$/.test(name)) {
    return null;
  }
  const response = await fetch(
    `https://cdn.jsdelivr.net/npm/@iconify-icons/${prefix}/${encodeURIComponent(name)}.js`
  );
  if (!response.ok) {
    return null;
  }
  const text = await response.text();
  const start = text.indexOf('{');
  const end = text.lastIndexOf('}');
  if (start < 0 || end <= start) {
    return null;
  }
  const data = JSON.parse(text.slice(start, end + 1));
  if (!data?.body || typeof data.body !== 'string' || data.body.includes('<script')) {
    return null;
  }
  const left = data.left || 0;
  const top = data.top || 0;
  const width = data.width || 16;
  const height = data.height || 16;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="${left} ${top} ${width} ${height}">${data.body}</svg>`;
};

/**
 * Descargar SVG de un icono. Primero el paquete npm; si no está, la API pública.
 */
const downloadIconSVG = async (iconId: string): Promise<string | null> => {
  try {
    const fromPackage = await svgFromIconifyPackage(iconId);
    if (fromPackage) {
      return fromPackage;
    }
    const response = await fetch(iconifySvgUrl(iconId));
    if (!response.ok) {
      return null;
    }
    return await response.text();
  } catch (error) {
    console.error('Error descargando icono:', error);
    return null;
  }
};

/**
 * Verificar si un icono está en caché
 */
export const isIconCached = (iconId: string): boolean => {
  const cached = getCachedIcons();
  return cached.has(iconId);
};

/**
 * Obtener SVG desde caché
 */
export const getCachedIconSVG = (iconId: string): string | null => {
  const cached = getCachedIcons();
  return cached.get(iconId)?.svg || null;
};

// ============================================
// TRADUCCIÓN CON GOOGLE TRANSLATE API
// ============================================

/**
 * Obtener caché de traducciones
 */
const getTranslationCache = (): Map<string, string> => {
  try {
    const cached = localStorage.getItem(TRANSLATION_CACHE_KEY);
    if (!cached) return new Map();
    
    const data = JSON.parse(cached);
    if (data.version !== TRANSLATION_CACHE_VERSION) {
      localStorage.removeItem(TRANSLATION_CACHE_KEY);
      return new Map();
    }
    
    return new Map(data.translations || []);
  } catch (error) {
    console.error('Error leyendo caché de traducciones:', error);
    return new Map();
  }
};

/**
 * Guardar traducción en caché
 */
const saveTranslationCache = (cache: Map<string, string>) => {
  try {
    const data = {
      version: TRANSLATION_CACHE_VERSION,
      translations: Array.from(cache.entries())
    };
    localStorage.setItem(TRANSLATION_CACHE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error guardando caché de traducciones:', error);
  }
};

/**
 * Traducir texto de español a inglés usando Google Translate API
 */
export const translateToEnglish = async (text: string): Promise<string> => {
  if (!text || !text.trim()) {
    return text;
  }
  
  const trimmedText = text.trim().toLowerCase();
  
  // Verificar caché primero
  const cache = getTranslationCache();
  if (cache.has(trimmedText)) {
    return cache.get(trimmedText)!;
  }
  
  // Obtener API key desde configuración
  const apiKey = config.GOOGLE_TRANSLATE_API_KEY || 
                 process.env.REACT_APP_GOOGLE_TRANSLATE_API_KEY || '';
  
  if (!apiKey) {
    // Si no hay API key, devolver el texto original
    console.warn('Google Translate API key no configurada. Usando texto original.');
    console.log('Config GOOGLE_TRANSLATE_API_KEY:', config.GOOGLE_TRANSLATE_API_KEY);
    console.log('Env REACT_APP_GOOGLE_TRANSLATE_API_KEY:', process.env.REACT_APP_GOOGLE_TRANSLATE_API_KEY);
    return text;
  }
  
  console.log('Traduciendo:', text, '-> usando API key:', apiKey.substring(0, 10) + '...');
  
  try {
    // Llamar a Google Translate API REST
    const url = `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        q: text,
        source: 'es',
        target: 'en',
        format: 'text'
      })
    });
    
    if (!response.ok) {
      await response.json().catch(() => ({}));
      console.error('Error en Google Translate API:', response.status);
      return text; // Fallback al texto original
    }
    
    const data = await response.json();
    const translatedText = data.data?.translations?.[0]?.translatedText || text;
    console.log('Traducción exitosa:', text, '->', translatedText);
    
    // Guardar en caché
    cache.set(trimmedText, translatedText.toLowerCase());
    saveTranslationCache(cache);
    
    return translatedText;
  } catch (error) {
    console.error('Error traduciendo texto:', error);
    return text; // Fallback al texto original
  }
};

/**
 * Detectar si un texto parece estar en inglés (simple heurística)
 */
const isLikelyEnglish = (text: string): boolean => {
  // Si no tiene caracteres especiales del español (ñ, acentos), probablemente es inglés
  const spanishChars = /[áéíóúñÁÉÍÓÚÑ]/;
  return !spanishChars.test(text);
};

// ============================================
// BÚSQUEDA EN ICONIFY API
// ============================================

/**
 * Buscar iconos en Iconify API por palabra clave
 */
const searchIconifyIconsInternal = async (
  keyword: string,
  limit: number = 999,  // Máximo permitido por API de Iconify
  useFallback: boolean = true  // Si es true, intenta búsqueda alternativa si no hay resultados
): Promise<IconifyIcon[]> => {
  if (!keyword || keyword.trim().length < 2) {
    return [];
  }
  
  try {
    // Usar búsqueda más específica con parámetros adicionales para mejor relevancia
    // La API de Iconify busca en nombres de iconos, así que usamos el keyword tal cual
    // NO usar prefixes para obtener TODOS los iconos, no solo de colecciones específicas
    // Esto permite encontrar iconos "outlined" y de otras colecciones
    const url = `https://api.iconify.design/search?query=${encodeURIComponent(keyword)}&limit=${limit}`;
    
    console.log('[searchIconifyIconsInternal] URL de búsqueda:', url);
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // La API devuelve un array de strings en formato "collection:icon-name"
    // Formatear resultados
    if (!data.icons || !Array.isArray(data.icons)) {
      // Si no hay resultados y el keyword tiene guiones, intentar búsqueda alternativa
      if (useFallback && keyword.includes('-') && !keyword.includes(':')) {
        const firstWord = keyword.split('-')[0];
        console.log('[searchIconifyIconsInternal] No hay resultados, intentando búsqueda alternativa con:', firstWord);
        return await searchIconifyIconsInternal(firstWord, limit, false); // false para evitar recursión infinita
      }
      return [];
    }
    
    const icons: IconifyIcon[] = data.icons.map((iconName: string) => {
      const [collection, ...nameParts] = iconName.split(':');
      const name = nameParts.join(':');
      // Extraer nombre limpio: quitar prefijos comunes como "material-symbols", "outline, etc.
      // Primero, extraer solo la parte del nombre (sin la colección)
      let cleanName = name || iconName;
      
      // Si el nombre contiene "material-symbols" o similar, extraer solo la parte útil
      // Ejemplo: "material-symbols:oven-outline" -> "oven"
      if (cleanName.includes('material-symbols') || cleanName.includes('material-symbol')) {
        // Extraer solo la parte después de "material-symbols:" o similar
        const parts = cleanName.split(/[-:]/);
        // Buscar la primera palabra que no sea "material", "symbol", "symbols", "outline", "filled", etc.
        const skipWords = ['material', 'symbol', 'symbols', 'outline', 'filled', 'round', 'sharp', 'thin'];
        cleanName = parts.find(part => part && !skipWords.includes(part.toLowerCase())) || cleanName;
      }
      
      // Remover palabras comunes que no aportan información
      cleanName = cleanName
        .replace(/\bmaterial\s*symbols?\b/gi, '')
        .replace(/\boutline\b/gi, '')
        .replace(/\bfilled\b/gi, '')
        .replace(/\bround\b/gi, '')
        .replace(/\bsharp\b/gi, '')
        .replace(/\bthin\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
      
      // Si quedó vacío o solo tiene "symbol", usar el nombre original sin la colección
      if (!cleanName || cleanName.length < 2 || cleanName.toLowerCase() === 'symbol' || cleanName.toLowerCase() === 'symbols') {
        // Extraer solo la parte del nombre después de ":"
        const nameOnly = iconName.split(':')[1] || iconName;
        // Remover variantes comunes
        cleanName = nameOnly
          .replace(/-outline$/i, '')
          .replace(/-filled$/i, '')
          .replace(/-round$/i, '')
          .replace(/-sharp$/i, '')
          .replace(/-thin$/i, '')
          .replace(/-/g, ' ')
          .trim();
        // Si aún está vacío, usar el nombre completo
        if (!cleanName || cleanName.length < 2) {
          cleanName = nameOnly.replace(/-/g, ' ') || iconName;
        }
      }
      
      return {
        name: iconName, // formato completo: "mdi:chef-hat"
        collection: collection || '',
        displayName: cleanName
      };
    });
    
    // Si no hay iconos y el keyword tiene guiones, intentar búsqueda alternativa
    if (icons.length === 0 && useFallback && keyword.includes('-') && !keyword.includes(':')) {
      const firstWord = keyword.split('-')[0];
      const remainingParts = keyword.split('-').slice(1); // Todas las partes después de la primera
      console.log('[searchIconifyIconsInternal] No hay iconos, intentando búsqueda alternativa con:', firstWord);
      const fallbackResults = await searchIconifyIconsInternal(firstWord, limit, false); // false para evitar recursión infinita
      
      // Filtrar los resultados para incluir solo los que contienen todas las partes del keyword original
      if (remainingParts.length > 0) {
        const filtered = fallbackResults.filter(icon => {
          const iconNameLower = icon.name.toLowerCase();
          // El icono debe contener todas las partes del keyword original
          return remainingParts.every(part => iconNameLower.includes(part.toLowerCase()));
        });
        console.log('[searchIconifyIconsInternal] Resultados después de filtrar por partes:', filtered.length);
        return filtered.length > 0 ? filtered : fallbackResults; // Si no hay filtrados, devolver todos
      }
      
      return fallbackResults;
    }
    
    // Filtrar y ordenar iconos por relevancia
    const keywordLower = keyword.toLowerCase();
    
    console.log('[searchIconifyIconsInternal] Total iconos recibidos de API:', icons.length);
    console.log('[searchIconifyIconsInternal] Buscando con keyword:', keywordLower);
    
    // Si no hay iconos, retornar vacío
    if (icons.length === 0) {
      console.log('[searchIconifyIconsInternal] No hay iconos para filtrar');
      return [];
    }
    
    // Detectar si el usuario está buscando un nombre específico de icono (contiene guiones)
    const isSpecificIconName = keyword.includes('-') && !keyword.includes(':');
    
    // Extraer palabras clave para búsqueda más precisa
    const keywordWords = keywordLower.split(/[\s-]+/).filter(w => w.length > 0);
    const mainKeyword = keywordWords[0]; // Primera palabra (la más importante)
    
    // Filtrar iconos: mostrar los que contienen la palabra clave de manera relevante
    let filteredIcons = icons.filter(icon => {
      const iconNameLower = icon.name.toLowerCase();
      const displayNameLower = icon.displayName?.toLowerCase() || '';
      
      // Priorizar iconos cuyo nombre contiene la palabra clave principal
      // Ejemplo: "oven" debe estar en "mdi:oven", "ph:oven-thin", "material-symbols:oven-outline"
      // Pero NO en "discover", "movement", etc.
      
      // Caso 1: Si es un nombre específico (tiene guiones), buscar coincidencia exacta o parcial
      if (isSpecificIconName) {
        // Para "oven-thin", buscar que contenga "oven" Y "thin" (en ese orden o cerca)
        const hasAllWords = keywordWords.every(word => 
          iconNameLower.includes(word) || displayNameLower.includes(word)
        );
        if (!hasAllWords) return false;
        
        // Priorizar los que tienen las palabras juntas o en secuencia
        const wordsTogether = iconNameLower.includes(keywordLower.replace(/\s+/g, '-')) || 
                             iconNameLower.includes(keywordLower.replace(/\s+/g, ''));
        return wordsTogether || hasAllWords;
      }
      
      // Caso 2: Búsqueda simple (una palabra o varias sin guiones)
      // El nombre del icono debe contener la palabra clave principal
      // Pero evitar coincidencias parciales irrelevantes (ej: "oven" en "discover")
      const hasMainKeyword = iconNameLower.includes(mainKeyword) || displayNameLower.includes(mainKeyword);
      if (!hasMainKeyword) return false;
      
      // Si hay múltiples palabras, todas deben estar presentes
      if (keywordWords.length > 1) {
        return keywordWords.every(word => 
          iconNameLower.includes(word) || displayNameLower.includes(word)
        );
      }
      
      // Para una sola palabra, verificar que no sea una coincidencia parcial irrelevante
      // Ejemplo: "oven" debe estar como palabra completa o parte de "oven-outline", no en "discover"
      // Buscar que la palabra esté después de ":" o como parte de un nombre de icono válido
      const afterColon = iconNameLower.split(':')[1] || '';
      return afterColon.includes(mainKeyword);
    });
    
    console.log('[searchIconifyIconsInternal] Iconos después de filtrar:', filteredIcons.length);
    
    // Ordenar por relevancia: primero los que tienen coincidencia más exacta
    const sortedIcons = filteredIcons.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aAfterColon = aName.split(':')[1] || '';
      const bAfterColon = bName.split(':')[1] || '';
      
      // Prioridad 1: Coincidencia exacta completa (el nombre después de ":" es igual a la palabra clave)
      const aExactMatch = aAfterColon === keywordLower || aAfterColon === keywordLower.replace(/\s+/g, '-');
      const bExactMatch = bAfterColon === keywordLower || bAfterColon === keywordLower.replace(/\s+/g, '-');
      if (aExactMatch && !bExactMatch) return -1;
      if (!aExactMatch && bExactMatch) return 1;
      
      // Prioridad 2: El nombre termina con la palabra clave
      const aEndsWith = aAfterColon.endsWith(keywordLower) || aAfterColon.endsWith(`-${keywordLower}`);
      const bEndsWith = bAfterColon.endsWith(keywordLower) || bAfterColon.endsWith(`-${keywordLower}`);
      if (aEndsWith && !bEndsWith) return -1;
      if (!aEndsWith && bEndsWith) return 1;
      
      // Prioridad 3: El nombre empieza con la palabra clave (después de ":")
      const aStartsWith = aAfterColon.startsWith(keywordLower) || aAfterColon.startsWith(`${keywordLower}-`);
      const bStartsWith = bAfterColon.startsWith(keywordLower) || bAfterColon.startsWith(`${keywordLower}-`);
      if (aStartsWith && !bStartsWith) return -1;
      if (!bStartsWith && bStartsWith) return 1;
      
      // Prioridad 4: Contiene la palabra clave como parte del nombre (después de ":")
      const aHasKeyword = aAfterColon.includes(keywordLower);
      const bHasKeyword = bAfterColon.includes(keywordLower);
      if (aHasKeyword && !bHasKeyword) return -1;
      if (!aHasKeyword && bHasKeyword) return 1;
      
      // Prioridad 5: Para búsquedas con múltiples palabras, priorizar los que tienen todas juntas
      if (keywordWords.length > 1) {
        const aHasAllTogether = keywordWords.every(word => aAfterColon.includes(word));
        const bHasAllTogether = keywordWords.every(word => bAfterColon.includes(word));
        if (aHasAllTogether && !bHasAllTogether) return -1;
        if (!aHasAllTogether && bHasAllTogether) return 1;
      }
      
      // Si ambos tienen la misma relevancia, mantener orden original
      return 0;
    });
    
    // Si después del filtro hay muy pocos resultados (< 5), devolver todos pero ordenados
    if (sortedIcons.length < 5 && icons.length > 0) {
      // Si hay muy pocos resultados filtrados, devolver todos pero ordenados por relevancia
      return icons.sort((a, b) => {
        const aName = a.name.toLowerCase();
        const bName = b.name.toLowerCase();
        const aContains = aName.includes(keywordLower);
        const bContains = bName.includes(keywordLower);
        
        if (aContains && !bContains) return -1;
        if (!aContains && bContains) return 1;
        return 0;
      });
    }
    
    return sortedIcons;
  } catch (error) {
    console.error('Error buscando iconos en Iconify:', error);
    return [];
  }
};

/**
 * Buscar iconos en Iconify API con búsqueda dual (español + inglés traducido)
 */
export const searchIconifyIcons = async (
  keyword: string,
  limit: number = 999  // Máximo permitido por API de Iconify
): Promise<IconifyIcon[]> => {
  console.log('[searchIconifyIcons] Iniciando búsqueda para:', keyword);
  
  if (!keyword || keyword.trim().length < 2) {
    console.log('[searchIconifyIcons] Keyword muy corto, retornando vacío');
    return [];
  }
  
  const trimmedKeyword = keyword.trim();
  
  // Estrategia: SIEMPRE traducir primero (asumimos español por defecto)
  // Si la traducción da buenos resultados, priorizar esos
  // Solo buscar con el término original si parece inglés Y la traducción no dio buenos resultados
  
  let resultsOriginal: IconifyIcon[] = [];
  let resultsTranslated: IconifyIcon[] = [];
  
  // Verificar si parece estar claramente en inglés
  const isEnglish = isLikelyEnglish(trimmedKeyword);
  console.log('[searchIconifyIcons] ¿Parece inglés?', isEnglish);
  
  // Búsqueda 1: SIEMPRE traducir y buscar (asumimos español por defecto)
  console.log('[searchIconifyIcons] Traduciendo (asumiendo español por defecto)');
  let translatedKeyword = trimmedKeyword;
  try {
    translatedKeyword = await translateToEnglish(trimmedKeyword);
    console.log('[searchIconifyIcons] Traducción:', trimmedKeyword, '->', translatedKeyword);
    
    if (translatedKeyword.toLowerCase() !== trimmedKeyword.toLowerCase()) {
      console.log('[searchIconifyIcons] Búsqueda con término traducido:', translatedKeyword);
      resultsTranslated = await searchIconifyIconsInternal(translatedKeyword, limit);
      console.log('[searchIconifyIcons] Resultados traducidos:', resultsTranslated.length);
    } else {
      console.log('[searchIconifyIcons] Traducción igual al original, no buscando de nuevo');
    }
  } catch (error) {
    console.error('[searchIconifyIcons] Error traduciendo:', error);
  }
  
  // Búsqueda 2: Si parece inglés Y la traducción dio pocos resultados (< 10), buscar también con el término original
  // Esto captura casos donde el usuario escribió directamente en inglés
  // Pero solo si la traducción no fue exitosa
  if (isEnglish && resultsTranslated.length < 10) {
    console.log('[searchIconifyIcons] Búsqueda adicional: término parece inglés y traducción dio pocos resultados:', trimmedKeyword);
    resultsOriginal = await searchIconifyIconsInternal(trimmedKeyword, limit);
    console.log('[searchIconifyIcons] Resultados en inglés:', resultsOriginal.length);
  }
  
  // Si la traducción fue exitosa (cambió el término), usar SOLO los resultados traducidos
  // Esto evita mostrar resultados irrelevantes cuando se busca en español
  const wasTranslated = translatedKeyword.toLowerCase() !== trimmedKeyword.toLowerCase();
  
  let finalResults: IconifyIcon[];
  
  if (wasTranslated) {
    // Si se tradujo, usar SOLO los resultados traducidos (ya están bien filtrados)
    console.log('[searchIconifyIcons] Usando solo resultados traducidos (búsqueda en español)');
    finalResults = resultsTranslated;
  } else {
    // Si no se tradujo (ya estaba en inglés), combinar resultados
    const combinedResults = [...resultsTranslated, ...resultsOriginal];
    const uniqueResults = new Map<string, IconifyIcon>();
    
    combinedResults.forEach(icon => {
      if (!uniqueResults.has(icon.name)) {
        uniqueResults.set(icon.name, icon);
      }
    });
    
    // Ordenar resultados: priorizar los que contienen la palabra clave en el nombre
    const searchTerm = translatedKeyword.toLowerCase();
    finalResults = Array.from(uniqueResults.values()).sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aContains = aName.includes(searchTerm);
      const bContains = bName.includes(searchTerm);
      
      // Priorizar los que contienen la palabra clave
      if (aContains && !bContains) return -1;
      if (!aContains && bContains) return 1;
      
      // Si ambos contienen, priorizar los que están en resultsTranslated
      if (aContains && bContains) {
        const aInTranslated = resultsTranslated.some(r => r.name === a.name);
        const bInTranslated = resultsTranslated.some(r => r.name === b.name);
        if (aInTranslated && !bInTranslated) return -1;
        if (!aInTranslated && bInTranslated) return 1;
      }
      
      // Si ninguno contiene, mantener orden original
      return 0;
    });
  }
  
  const limitedResults = finalResults.slice(0, limit);
  console.log('[searchIconifyIcons] Resultados finales:', limitedResults.length, 'iconos encontrados');
  
  return limitedResults;
};

// ============================================
// RENDERIZADO DE ICONOS
// ============================================

/**
 * Renderizar icono Iconify (desde caché o descargar)
 */
export const renderIconifyIcon = (
  iconId: string, // formato: "mdi:chef-hat"
  size: number = 48,
  color?: string
): React.ReactNode => {
  // Verificar si está en caché
  const cached = isIconCached(iconId);
  
  if (cached) {
    // Renderizar desde caché (más rápido)
    const svg = getCachedIconSVG(iconId);
    if (svg) {
      return (
        <Box
          component="span"
          dangerouslySetInnerHTML={{ __html: svg }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: size,
            height: size,
            '& svg': {
              width: size,
              height: size,
              color: color || '#5C9EFF',
            }
          }}
        />
      );
    }
  }
  
  // Si no está en caché, usar componente Icon (descarga automáticamente)
  // Cachearlo en background de forma lazy para evitar problemas de inicialización
  if (typeof window !== 'undefined') {
    setTimeout(() => {
      cacheIcon(iconId).catch(err => {
        console.error('Error cacheando icono en background:', err);
      });
    }, 0);
  }
  
  return (
    <Icon 
      icon={iconId} 
      width={size} 
      height={size}
      style={{ color: color || '#5C9EFF' }}
    />
  );
};

/**
 * Renderizar preview de icono para selector (tamaño pequeño)
 */
export const renderIconifyPreview = (
  iconId: string,
  size: number = 24
): React.ReactNode => {
  return renderIconifyIcon(iconId, size);
};

/**
 * Pre-cachear iconos comunes (opcional, para mejor UX)
 */
export const preCacheCommonIcons = async (iconIds: string[]): Promise<void> => {
  const promises = iconIds.map(iconId => cacheIcon(iconId));
  await Promise.allSettled(promises);
};
